// PostHog product analytics — respects cookie consent (Senegal Loi 2008-12).
// PostHog is only initialized when the user has accepted cookies.

import posthog from "posthog-js";

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || "https://app.posthog.com";

const CONSENT_KEY = "cieda_cookie_consent";

export type CookieConsent = "accepted" | "rejected" | null;

export function getStoredConsent(): CookieConsent {
  const value = localStorage.getItem(CONSENT_KEY);
  if (value === "accepted" || value === "rejected") return value;
  return null;
}

export function setConsent(consent: "accepted" | "rejected"): void {
  localStorage.setItem(CONSENT_KEY, consent);
  if (consent === "accepted") {
    initPostHog();
  } else {
    shutdownPostHog();
  }
}

export function initPostHog(): void {
  if (!POSTHOG_KEY) {
    console.warn("[PostHog] VITE_POSTHOG_KEY not set — analytics disabled");
    return;
  }

  const consent = getStoredConsent();
  if (consent !== "accepted") {
    return;
  }

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    capture_pageview: true,
    capture_pageleave: true,
    autocapture: true,
    persistence: "localStorage",
    // Session replay only after consent
    disable_session_recording: false,
    // Do not track users who have opted out
    opt_out_capturing_by_default: false,
    // Respect Do Not Track browser setting
    respect_dnt: true,
    // Mask all text in session replay for privacy
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: "*",
    },
  });
}

function shutdownPostHog(): void {
  try {
    posthog.opt_out_capturing();
  } catch {
    // PostHog may not be initialized yet — safe to ignore
  }
}

// Custom event tracking helpers
export const analytics = {
  productViewed(productId: string, brand: string, priceFcfa: number): void {
    posthog.capture("product_viewed", {
      product_id: productId,
      brand,
      price_fcfa: priceFcfa,
    });
  },

  addedToCart(productId: string, quantity: number, priceFcfa: number): void {
    posthog.capture("added_to_cart", {
      product_id: productId,
      quantity,
      price_fcfa: priceFcfa,
    });
  },

  checkoutStarted(cartTotalFcfa: number, itemCount: number): void {
    posthog.capture("checkout_started", {
      cart_total_fcfa: cartTotalFcfa,
      item_count: itemCount,
    });
  },

  paymentMethodSelected(method: "wave" | "orange_money"): void {
    posthog.capture("payment_method_selected", { method });
  },

  orderCompleted(orderRef: string, totalFcfa: number, paymentMethod: string): void {
    posthog.capture("order_completed", {
      order_ref: orderRef,
      total_fcfa: totalFcfa,
      payment_method: paymentMethod,
    });
  },

  accountCreated(source: "post_checkout" | "direct"): void {
    posthog.capture("account_created", { source });
  },
} as const;
